import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getChannel, createVideo, updateVideo, deleteVideo, getEditors, getStageHistory, getLatestComments, bulkUpdateVideos, bulkDeleteVideos, getChannels, getCompletedVideos, moveToCompleted } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { VideoEditForm } from "../components/VideoEditForm";
import { StageHistoryDialog } from "../components/StageHistoryDialog";
import { BulkActionBar } from "../components/channel/BulkActionBar";
import { VideoTabs } from "../components/channel/VideoTabs";
import { ActiveVideosTable } from "../components/channel/ActiveVideosTable";
import { CompletedVideosTable } from "../components/channel/CompletedVideosTable";
import { STAGES } from "../components/channel/constants";
import { ArrowLeft, Plus, FilmStrip } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function ChannelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();

  const [channel, setChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);
  const [filter, setFilter] = useState({ stage: "", type: "" });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [stageHistory, setStageHistory] = useState([]);
  const [historyVideoTitle, setHistoryVideoTitle] = useState("");
  const [latestComments, setLatestComments] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [allChannels, setAllChannels] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [completedVideos, setCompletedVideos] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    is_long: true,
    is_short: false,
    shorts_count: 0,
    stage: "idea",
    priority: null,
    editor_id: "",
    scheduled_date: "",
    notes: "",
    short_required: false,
    uploaded_youtube: false,
    uploaded_facebook: false,
    uploaded_tiktok: false,
    drive_folder_url: "",
    script_url: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [channelRes, editorsRes, commentsRes, channelsRes] = await Promise.all([
        getChannel(id),
        getEditors(),
        getLatestComments(),
        getChannels(),
      ]);
      setChannel(channelRes.data);
      setVideos((channelRes.data.videos || []).filter(v => !v.completed));
      setEditors(editorsRes.data);
      setLatestComments(commentsRes.data || {});
      setAllChannels(channelsRes.data || []);
      try {
        const completedRes = await getCompletedVideos();
        setCompletedVideos((completedRes.data || []).filter(v => v.channel_id === id));
      } catch { setCompletedVideos([]); }
    } catch {
      toast.error("Failed to load channel");
      navigate("/channels");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refreshLatestComments = useCallback(async () => {
    try {
      const res = await getLatestComments();
      setLatestComments(res.data || {});
    } catch { /* silent */ }
  }, []);

  const handleOpenCreate = () => {
    setEditingVideo(null);
    setFormData({
      title: "", is_long: true, is_short: false, shorts_count: 0, stage: "idea",
      priority: null, editor_id: "", scheduled_date: "", notes: "",
      short_required: false, uploaded_youtube: false, uploaded_facebook: false,
      uploaded_tiktok: false, drive_folder_url: "", script_url: "",
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      is_long: video.is_long !== undefined ? video.is_long : true,
      is_short: video.is_short !== undefined ? video.is_short : false,
      shorts_count: video.shorts_count || 0,
      stage: video.stage,
      priority: video.priority,
      editor_id: video.editor_id || "",
      scheduled_date: video.scheduled_date || "",
      notes: video.notes || "",
      short_required: video.short_required || false,
      uploaded_youtube: video.uploaded_youtube || false,
      uploaded_facebook: video.uploaded_facebook || false,
      uploaded_tiktok: video.uploaded_tiktok || false,
      drive_folder_url: video.drive_folder_url || "",
      script_url: video.script_url || "",
    });
    setDialogOpen(true);
  };

  const handleOpenHistory = async (video) => {
    try {
      const res = await getStageHistory(video.id);
      setStageHistory(res.data.history || []);
      setHistoryVideoTitle(res.data.title || video.title);
      setHistoryDialogOpen(true);
    } catch {
      toast.error("Failed to load stage history");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVideo) {
        await updateVideo(editingVideo.id, formData);
        toast.success("Video updated");
      } else {
        await createVideo({ ...formData, channel_id: id });
        toast.success("Video created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save video");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteVideo(deletingVideo.id);
      toast.success("Video deleted");
      setDeleteDialogOpen(false);
      setDeletingVideo(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete video");
    }
  };

  const handleInlineUpdate = async (videoId, field, value) => {
    try {
      await updateVideo(videoId, { [field]: value });
      toast.success("Updated");
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleBulkApply = async () => {
    if (!bulkField || !bulkValue || selectedIds.length === 0) return;
    const payload = { video_ids: selectedIds };
    if (bulkField === "priority") payload.priority = parseInt(bulkValue);
    if (bulkField === "stage") payload.stage = bulkValue;
    if (bulkField === "editor") payload.editor_id = bulkValue === "__unassign" ? "" : bulkValue;
    if (bulkField === "type") {
      payload.is_long = bulkValue === "long";
      payload.is_short = bulkValue === "short";
    }
    if (bulkField === "channel") payload.channel_id = bulkValue;
    try {
      const res = await bulkUpdateVideos(payload);
      toast.success(`Updated ${res.data.updated} video(s)`);
      setSelectedIds([]);
      setBulkField("");
      setBulkValue("");
      if (bulkField === "channel") {
        navigate(`/channels/${bulkValue}`);
      } else {
        fetchData();
      }
    } catch {
      toast.error("Bulk update failed");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} video(s)? This cannot be undone.`)) return;
    try {
      const res = await bulkDeleteVideos({ video_ids: selectedIds });
      toast.success(`Deleted ${res.data.deleted} video(s)`);
      setSelectedIds([]);
      fetchData();
    } catch {
      toast.error("Bulk delete failed");
    }
  };

  const handleMoveToCompleted = async () => {
    const publishedSelected = selectedIds.filter(sid => {
      const v = filteredVideos.find(fv => fv.id === sid);
      return v && v.stage === "published";
    });
    if (publishedSelected.length === 0) {
      toast.error("Only published videos can be moved to completed");
      return;
    }
    if (!window.confirm(`Move ${publishedSelected.length} published video(s) to Completed?`)) return;
    try {
      const res = await moveToCompleted({ video_ids: publishedSelected });
      toast.success(`Moved ${res.data.completed} video(s) to completed`);
      setSelectedIds([]);
      fetchData();
    } catch {
      toast.error("Failed to move to completed");
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const filteredVideos = useMemo(() => videos
    .filter((video) => {
      if (filter.stage && video.stage !== filter.stage) return false;
      if (filter.type === "long" && !video.is_long) return false;
      if (filter.type === "short" && !video.is_short) return false;
      return true;
    })
    .sort((a, b) => {
      if (a.stage === "published" && b.stage !== "published") return 1;
      if (b.stage === "published" && a.stage !== "published") return -1;
      if (!sortConfig.key) return 0;
      const dir = sortConfig.direction === "asc" ? 1 : -1;
      const key = sortConfig.key;
      if (key === "title") return dir * (a.title || "").localeCompare(b.title || "");
      if (key === "type") {
        const typeA = a.is_long ? "long" : "short";
        const typeB = b.is_long ? "long" : "short";
        return dir * typeA.localeCompare(typeB);
      }
      if (key === "stage") return dir * (STAGES.indexOf(a.stage) - STAGES.indexOf(b.stage));
      if (key === "priority") return dir * ((a.priority ?? 99) - (b.priority ?? 99));
      if (key === "editor") return dir * (a.editor_name || "zzz").localeCompare(b.editor_name || "zzz");
      if (key === "scheduled") return dir * (a.scheduled_date || "").localeCompare(b.scheduled_date || "");
      if (key === "comment") {
        const cA = latestComments[a.id]?.text || "";
        const cB = latestComments[b.id]?.text || "";
        return dir * cA.localeCompare(cB);
      }
      return 0;
    }), [videos, filter, sortConfig, latestComments]);

  const canEdit = hasPermission("edit_videos", id);
  const canCreate = hasPermission("create_videos", id);
  const canDelete = hasPermission("delete_videos", id);
  const isEditor = user?.role === "editor";
  const isAdmin = user?.role === "owner" || user?.role === "admin";

  const editorColorMap = useMemo(() => {
    const map = {};
    editors.forEach((e) => { map[e.id] = e.color || "#3B82F6"; });
    return map;
  }, [editors]);

  const getRowHighlight = (video) => {
    if (video.stage === "published") return "";
    if (video.stage === "ready") return "border-l-4 border-l-amber-400 bg-amber-500/20";
    if (video.priority === 1 && video.editor_id && editorColorMap[video.editor_id]) {
      return "border-l-4";
    }
    return "";
  };

  const getRowStyle = (video) => {
    if (video.stage === "published" || video.stage === "ready" || video.priority !== 1 || !video.editor_id) return {};
    const color = editorColorMap[video.editor_id];
    if (!color) return {};
    return { borderLeftColor: color, backgroundColor: `${color}25` };
  };

  const myEditorId = user?.editor_id;
  const isMyVideo = (video) => !isEditor || video.editor_id === myEditorId;

  const toggleSelectAll = () => {
    setSelectedIds(selectedIds.length === filteredVideos.length ? [] : filteredVideos.map((v) => v.id));
  };

  const toggleSelect = (vid) => {
    setSelectedIds((prev) => prev.includes(vid) ? prev.filter((x) => x !== vid) : [...prev, vid]);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedIds([]);
  };

  const handleClearBulk = () => {
    setSelectedIds([]);
    setBulkField("");
    setBulkValue("");
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64" />
        <div className="skeleton h-64 w-full" />
      </div>
    );
  }

  if (!channel) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/channels" className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${channel.color}20` }}>
            <FilmStrip size={24} style={{ color: channel.color }} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-white">{channel.name}</h1>
            <p className="text-gray-400 text-sm">{channel.niche} &bull; {channel.posting_schedule}</p>
          </div>
        </div>
        {canCreate && (
          <Button onClick={handleOpenCreate} className="btn-primary" data-testid="create-video-btn">
            <Plus size={18} className="mr-2" />
            New Video
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STAGES.slice(0, 4).map((stage) => {
          const count = videos.filter((v) => v.stage === stage).length;
          return (
            <div key={stage} className="surface-card p-4">
              <p className="text-sm text-gray-400 capitalize">{stage}</p>
              <p className="text-2xl font-heading font-semibold text-white">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={filter.stage} onValueChange={(v) => setFilter({ ...filter, stage: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A2E] border-white/10">
            <SelectItem value="all" className="text-white">All Stages</SelectItem>
            {STAGES.map((s) => (
              <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v === "all" ? "" : v })}>
          <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A2E] border-white/10">
            <SelectItem value="all" className="text-white">All Types</SelectItem>
            <SelectItem value="long" className="text-white">Long</SelectItem>
            <SelectItem value="short" className="text-white">Short</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-gray-400">{filteredVideos.length} videos</span>
      </div>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClearSelection={handleClearBulk}
        bulkField={bulkField}
        onBulkFieldChange={(v) => { setBulkField(v); setBulkValue(""); }}
        bulkValue={bulkValue}
        onBulkValueChange={setBulkValue}
        onBulkApply={handleBulkApply}
        onBulkDelete={handleBulkDelete}
        onMoveToCompleted={handleMoveToCompleted}
        canDelete={canDelete}
        isAdmin={isAdmin}
        editors={editors}
        allChannels={allChannels}
        channelId={id}
      />

      {/* Tabs */}
      <VideoTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        activeCount={filteredVideos.length}
        completedCount={completedVideos.length}
      />

      {/* Content */}
      {activeTab === "active" ? (
        <ActiveVideosTable
          videos={filteredVideos}
          selectedIds={selectedIds}
          onToggleSelectAll={toggleSelectAll}
          onToggleSelect={toggleSelect}
          sortConfig={sortConfig}
          onSort={handleSort}
          canEdit={canEdit}
          canDelete={canDelete}
          isEditor={isEditor}
          isAdmin={isAdmin}
          isMyVideo={isMyVideo}
          editorColorMap={editorColorMap}
          editors={editors}
          getRowHighlight={getRowHighlight}
          getRowStyle={getRowStyle}
          onInlineUpdate={handleInlineUpdate}
          onOpenHistory={handleOpenHistory}
          onOpenEdit={handleOpenEdit}
          onRequestDelete={(video) => { setDeletingVideo(video); setDeleteDialogOpen(true); }}
          latestComments={latestComments}
          onRefreshLatestComments={refreshLatestComments}
        />
      ) : (
        <CompletedVideosTable videos={completedVideos} />
      )}

      {/* Create/Edit Dialog */}
      <VideoEditForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        editingVideo={editingVideo}
        editors={editors}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Video?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete &quot;{deletingVideo?.title}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stage History Dialog */}
      <StageHistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        title={historyVideoTitle}
        history={stageHistory}
      />
    </div>
  );
}
